<template>
  <div>
    <label v-if="label" class="label-input">{{ label }}</label>
    <div :class="['group-item', wrapperClass]">
      <v-row>
        <v-col cols="12" md="6">
          <v-checkbox
            :input-value="safeSettings.paddingTop"
            label="Top Padding"
            hide-details
            class="mt-0"
            @change="value => updateSettings('paddingTop', !!value)"
          />
        </v-col>
        <v-col cols="12" md="6">
          <v-checkbox
            :input-value="safeSettings.paddingBottom"
            label="Bottom Padding"
            hide-details
            class="mt-0"
            @change="value => updateSettings('paddingBottom', !!value)"
          />
        </v-col>
        <v-col cols="12" md="6">
          <ColorPicker
            :value="safeSettings.backgroundColor"
            name="background-color"
            label="Background Color"
            default-color="#FFFFFF"
            @input="value => updateSettings('backgroundColor', value)"
          />
        </v-col>
        <v-col cols="12" md="6">
          <ColorPicker
            :value="safeSettings.textColor"
            name="text-color"
            label="Text Color"
            default-color="#000000"
            @input="value => updateSettings('textColor', value)"
          />
        </v-col>
        <v-col v-if="useBackgroundMobile" cols="12" md="12">
          <v-checkbox
            :input-value="safeSettings.useBackgroundMobile"
            label="Use Background Mobile"
            hide-details
            class="mt-0"
            @change="value => updateSettings('useBackgroundMobile', !!value)"
          />
          <div v-if="safeSettings.useBackgroundMobile">
            <UploadImage
              :image="{ imageSrc: safeSettings.backgroundImageMobile }"
              :height="`120`"
              :typeUpload="'image'"
              @callback="handleUploadBackgroundImageMobile"
              @callbackDelete="handleRemoveBackgroundImageMobile"
            />
          </div>
        </v-col>
        <v-col v-if="showBackgroundImage" cols="12">
          <label class="d-block mb-2">Background Image</label>
          <UploadImage
            :image="{ imageSrc: safeSettings.backgroundImage }"
            :height="`120`"
            :typeUpload="'image'"
            @callback="handleUploadBackgroundImage"
            @callbackDelete="handleRemoveBackgroundImage"
          />
        </v-col>
        <v-col cols="12">
          <div class="d-flex align-center mb-2" style="gap: 10px">
            <label for="overlayShow" class="mb-0">Overlay</label>
            <input
              id="overlayShow"
              type="checkbox"
              :checked="safeSettings.showOverlay"
              @change="value => updateSettings('showOverlay', !!value.target.checked)"
            />
          </div>
          <div v-if="safeSettings.showOverlay" class="group-item">
            <v-row>
              <v-col cols="12" md="6">
                <ColorPicker
                  :value="safeSettings.overlayColor"
                  name="overlay-color"
                  label="Overlay Color"
                  default-color="#000000"
                  @input="value => updateSettings('overlayColor', value)"
                />
              </v-col>
              <v-col cols="12" md="6">
                <v-text-field
                  class="mt-2"
                  style="flex: 1"
                  :value="safeSettings.overlayOpacity"
                  type="number"
                  label="Overlay Opacity (0.0 - 1.0)"
                  min="0"
                  max="1"
                  step="0.1"
                  hide-details
                  dense
                  @input="value => updateSettings('overlayOpacity', normalizeOpacity(value))"
                />
              </v-col>
            </v-row>
          </div>
        </v-col>

        <v-col v-if="showContainerPage" cols="12">
          <v-switch
            :input-value="safeContainerPage.enableStyle"
            label="Enable container page"
            hide-details
            class="mt-0"
            @change="value => updateContainerPage('enableStyle', !!value)"
          />
        </v-col>
        <v-col v-if="showContainerPage && safeContainerPage.enableStyle" cols="12">
          <ColorPicker
            :value="safeContainerPage.background"
            name="container-background"
            label="Container Background"
            default-color="#FFFFFF"
            @input="value => updateContainerPage('background', value)"
          />
        </v-col>
      </v-row>
    </div>
  </div>
</template>

<script>
import UploadImage from '@/components/form/uploadFile/UpdateImage.vue';
import ColorPicker from './color-picker.vue';

const defaultSettings = {
  paddingTop: true,
  paddingBottom: true,
  backgroundColor: '#FFFFFF',
  textColor: '#000000',
  overlayColor: '#000000',
  overlayOpacity: 0,
  showOverlay: false,
  backgroundImage: '',
};

const defaultContainerPage = {
  enableStyle: false,
  background: '#FFFFFF',
};

export default {
  name: 'SectionSettingsContainerFuild',
  components: {
    UploadImage,
    ColorPicker,
  },
  props: {
    settings: {
      type: Object,
      required: true,
      default: () => ({ ...defaultSettings }),
    },
    containerPage: {
      type: Object,
      required: false,
      default: () => ({ ...defaultContainerPage }),
    },
    label: {
      type: String,
      default: 'Section Settings',
    },
    wrapperClass: {
      type: String,
      default: '',
    },
    showBackgroundImage: {
      type: Boolean,
      default: true,
    },
    showOverlayFields: {
      type: Boolean,
      default: true,
    },
    showContainerPage: {
      type: Boolean,
      default: true,
    },
    useBackgroundMobile: {
      type: Boolean,
      default: false,
    },
  },
  computed: {
    safeSettings() {
      return { ...defaultSettings, ...(this.settings || {}) };
    },
    safeContainerPage() {
      return { ...defaultContainerPage, ...(this.containerPage || {}) };
    },
  },
  methods: {
    updateSettings(key, value) {
      this.$set(this.settings, key, value);
      this.$emit('update:settings', this.settings);
      this.$emit('change', { settings: this.settings, containerPage: this.containerPage });
    },
    updateContainerPage(key, value) {
      this.$set(this.containerPage, key, value);
      this.$emit('update:containerPage', this.containerPage);
      this.$emit('change', { settings: this.settings, containerPage: this.containerPage });
    },
    handleUploadBackgroundImage(obj) {
      this.updateSettings('backgroundImage', (obj && obj.imageSrc) || '');
    },
    handleRemoveBackgroundImage() {
      this.updateSettings('backgroundImage', '');
    },
    handleUploadBackgroundImageMobile(obj) {
      this.updateSettings('backgroundImageMobile', (obj && obj.imageSrc) || '');
    },
    handleRemoveBackgroundImageMobile() {
      this.updateSettings('backgroundImageMobile', '');
    },
    normalizeOpacity(value) {
      const num = Number(value);
      if (Number.isNaN(num)) return 0;
      if (num < 0) return 0;
      if (num > 1) return 1;
      return num;
    },
  },
};
</script>

<style scoped lang="scss">
.group-item {
  padding: 15px 10px;
  border-radius: 10px;
  border: 1px solid #e0e0e0;
}
.label-input {
  font-size: 18px !important;
  font-weight: 500;
  color: #000;
  margin-bottom: 10px;
  display: block;
}
</style>
